import { useState, type CSSProperties, type ReactNode } from 'react';
import { useIntlayer } from 'react-intlayer';

import { ImageBackground } from '../ui/image-background';

import { FadeIn } from '~/components/ui/fade-in';
import { ProductCTASection } from '~/components/ui/ProductCTASection';
import { cn } from '~/lib/utils';

interface TeamMemberCard {
  key: string;
  name: string;
  role: string;
  imageSrc: string;
  objectPosition?: CSSProperties['objectPosition'];
}

interface ResponsiveImageProps {
  src: string;
  alt: string;
  className?: string;
  imageClassName?: string;
  objectPosition?: CSSProperties['objectPosition'];
  fallbackLabel: string;
}

const getInitials = (value: string) => {
  return value
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
};

const ResponsiveImage = ({
  src,
  alt,
  className,
  imageClassName,
  objectPosition = 'center',
  fallbackLabel,
}: ResponsiveImageProps) => {
  const [hasError, setHasError] = useState(false);

  return (
    <div className={cn('relative overflow-hidden bg-[#090b0e]', className)}>
      {!hasError ? (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        <img
          src={src}
          alt={alt}
          className={cn(
            'absolute inset-0 h-full w-full object-cover',
            imageClassName
          )}
          style={{ objectPosition }}
          onError={() => setHasError(true)}
        />
      ) : null}

      {hasError ? (
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(39,98,241,0.35),transparent_42%),linear-gradient(140deg,rgba(255,255,255,0.12),rgba(255,255,255,0.03)_35%,rgba(0,0,0,0.4)_100%)]" />
          <div className="absolute inset-0 opacity-20 [background-image:linear-gradient(rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:28px_28px]" />
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
            <span className="font-manrope text-[40px] font-medium leading-none text-white/90 md:text-[64px]">
              {getInitials(fallbackLabel)}
            </span>
            <span className="font-fragment-mono text-[12px] uppercase tracking-[0.12px] text-white/50 md:text-[14px] md:tracking-[0.14px]">
              {fallbackLabel}
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const SectionHeader = ({
  label,
  title,
}: {
  label: ReactNode;
  title: ReactNode;
}) => {
  return (
    <div className="flex flex-col gap-3 lg:gap-5 text-white">
      <FadeIn>
        <p className="font-fragment-mono text-sm uppercase tracking-[0.14px]">
          {label}
        </p>
      </FadeIn>
      <FadeIn delay={0.1}>
        <p className="font-manrope text-[28px] font-normal leading-normal md:text-[24px] lg:max-w-[628px] lg:text-[30px]">
          {title}
        </p>
      </FadeIn>
    </div>
  );
};

const TeamGrid = ({ members }: { members: TeamMemberCard[] }) => {
  return (
    <div className="grid grid-cols-1 gap-y-14 md:grid-cols-2 md:gap-6 md:gap-y-12 lg:grid-cols-3">
      {members.map((member, index) => (
        <FadeIn key={member.key} delay={0.06 * index}>
          <article className="flex flex-col gap-5">
            <ResponsiveImage
              src={member.imageSrc}
              alt={member.name}
              className="aspect-[410/532] w-full"
              objectPosition={member.objectPosition}
              fallbackLabel={member.name}
            />
            <div className="flex flex-col gap-2">
              <h3 className="font-manrope text-[20px] font-normal leading-[1.4] text-white">
                {member.name}
              </h3>
              <p className="font-fragment-mono text-xs uppercase tracking-[0.12px] text-graphite-700">
                {member.role}
              </p>
            </div>
          </article>
        </FadeIn>
      ))}
    </div>
  );
};

export const TeamPageContent = () => {
  const { intro, mainPhrase, executive, advisory } = useIntlayer('team-page');
  const { cta: aboutCta } = useIntlayer('about');

  const executiveMembers: TeamMemberCard[] = [
    {
      key: 'brooks',
      name: executive.members.brooks.name.value,
      role: executive.members.brooks.role.value,
      imageSrc: '/assets/images/brooks-newmark.png',
      objectPosition: 'center top',
    },
    {
      key: 'ivan',
      name: executive.members.ivan.name.value,
      role: executive.members.ivan.role.value,
      imageSrc: '/assets/images/ivan-matveichenko.png',
      objectPosition: 'center top',
    },
    {
      key: 'robert',
      name: executive.members.robert.name.value,
      role: executive.members.robert.role.value,
      imageSrc: '/assets/images/robert-seely.png',
      objectPosition: 'center top',
    },
    {
      key: 'oleksii',
      name: executive.members.oleksii.name.value,
      role: executive.members.oleksii.role.value,
      imageSrc: '/assets/images/oleksii-tretiak.png',
      objectPosition: 'center top',
    },
    {
      key: 'viktoriia',
      name: executive.members.viktoriia.name.value,
      role: executive.members.viktoriia.role.value,
      imageSrc: '/assets/images/viktoriia-kulchytska.png',
      objectPosition: 'center top',
    },
  ];

  const advisoryMembers: TeamMemberCard[] = [
    {
      key: 'david',
      name: advisory.members.david.name.value,
      role: advisory.members.david.role.value,
      imageSrc: '/assets/images/david-richards.png',
      objectPosition: 'center top',
    },
    {
      key: 'valeriy',
      name: advisory.members.valeriy.name.value,
      role: advisory.members.valeriy.role.value,
      imageSrc: '/assets/images/valeriy-kondratyuk.png',
      objectPosition: 'center top',
    },
    {
      key: 'jade',
      name: advisory.members.jade.name.value,
      role: advisory.members.jade.role.value,
      imageSrc: '/assets/images/jade-mcglynn1.png',
      objectPosition: 'center top',
    },
    {
      key: 'ralph',
      name: advisory.members.ralph.name.value,
      role: advisory.members.ralph.role.value,
      imageSrc: '/assets/images/ralph-goff.png',
      objectPosition: 'center top',
    },
    {
      key: 'glenn',
      name: advisory.members.glenn.name.value,
      role: advisory.members.glenn.role.value,
      imageSrc: '/assets/images/glenn-corn1.png',
      objectPosition: 'center top',
    },
  ];

  return (
    <div className="min-h-full w-full bg-black">
      <section className="px-4 pt-[130px] md:px-8 lg:px-20 lg:pt-[200px]">
        <div className="flex w-full flex-col gap-[10px] lg:gap-5">
          <FadeIn>
            <p className="font-fragment-mono text-sm uppercase tracking-[0.14px] text-white">
              {intro.label}
            </p>
          </FadeIn>
          <FadeIn delay={0.12}>
            <p className="font-manrope text-[24px] font-normal leading-normal tracking-[0.24px] text-white md:max-w-[431px] md:text-[30px] md:tracking-normal lg:max-w-[628px]">
              {intro.description}
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="mt-10 lg:mt-[72px] relative w-full h-[400px] md:h-[705px] lg:h-[810px]">
        <ImageBackground
          imageUrl="/assets/images/founders.jpg"
          className=""
          backgroundPosition="center top"
        />
      </section>

      <section className="px-4 py-[120px] md:px-8 lg:px-20 lg:py-[200px]">
        <div className="mx-auto max-w-[1280px] text-center text-white">
          <FadeIn>
            <h1 className="font-manrope text-[40px] font-normal leading-[1.15] md:text-[64px] md:leading-[1.2] lg:text-[96px]">
              {mainPhrase.line1}
              <br />
              {mainPhrase.line2}
            </h1>
          </FadeIn>
        </div>
      </section>

      <section className="px-4 pb-[80px] md:px-8 lg:px-20 lg:pb-[160px]">
        <div className="flex flex-col gap-10 md:gap-12">
          <SectionHeader label={executive.label} title={executive.title} />
          <TeamGrid members={executiveMembers} />
        </div>
      </section>

      <section className="px-4 pb-[80px] md:px-8 lg:px-20 lg:pb-[120px]">
        <div className="flex flex-col gap-10 md:gap-12">
          <SectionHeader label={advisory.label} title={advisory.title} />
          <TeamGrid members={advisoryMembers} />
        </div>
      </section>

      <ProductCTASection
        title={aboutCta.label}
        heading={aboutCta.heading}
        button1Text={aboutCta.button}
        backgroundImage="/assets/images/chorna_cta.jpg"
        imageClassName="flip-horizontal"
      />
    </div>
  );
};
